// src/app/pages/admin/AdminTradeManagement.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { adminApi } from '@app/features/admin/adminApi';

// Status badge component
function StatusBadge({ status }) {
  const statusMap = {
    'PENDING_MODERATION': { label: 'Pending Approval', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
    'APPROVED': { label: 'Approved', className: 'bg-green-500/20 text-green-300 border-green-500/30' },
    'REJECTED': { label: 'Rejected', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
    'RESOLVED': { label: 'Resolved', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    'CANCELLED': { label: 'Cancelled', className: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  };
  
  const info = statusMap[status] || { label: status, className: 'bg-gray-500/20 text-gray-300 border-gray-500/30' };
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${info.className}`}>
      {info.label}
    </span>
  );
}

// Outcome badge component
function OutcomeBadge({ outcome }) {
  const outcomeMap = {
    'WIN': { label: 'Win', className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: TrendingUp },
    'LOSE': { label: 'Loss', className: 'bg-red-500/20 text-red-300 border-red-500/30', icon: TrendingDown },
    'PUSH': { label: 'Push', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: Minus },
  };
  
  const info = outcomeMap[outcome] || { label: outcome || 'Unknown', className: 'bg-gray-500/20 text-gray-300 border-gray-500/30', icon: AlertCircle };
  const Icon = info.icon;
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full border flex items-center gap-1 ${info.className}`}>
      <Icon size={12} />
      {info.label}
    </span>
  );
}

// Trade card component for pending decisions
function PendingTradeCard({ trade, onSetResult }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [note, setNote] = useState('');
  
  const handleConfirm = () => {
    if (!selectedOutcome) return;
    onSetResult(trade.id, selectedOutcome, note);
  };
  
  const timeRemaining = trade.resolveAt ? 
    Math.max(0, Math.floor((new Date(trade.resolveAt) - new Date()) / 60000)) : 0;
  
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 hover:bg-gray-900/80 transition-colors">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
              {trade.market?.slice(0, 3) || '??'}
            </div>
            <div>
              <div className="font-semibold text-white">{trade.market}</div>
              <div className="text-xs text-gray-400">
                ID: {trade.id?.slice(0, 8)}... • {trade.direction} • ${Number(trade.stakeAmount).toLocaleString()}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <StatusBadge status={trade.status} />
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 rounded-lg hover:bg-gray-800 text-gray-400"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-400">User ID</div>
                <div className="text-white">{trade.userId?.slice(0, 12)}...</div>
              </div>
              <div>
                <div className="text-gray-400">Stake</div>
                <div className="text-white">${Number(trade.stakeAmount).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-400">Payout Multiplier</div>
                <div className="text-white">{trade.payoutMultiplier}x</div>
              </div>
              <div>
                <div className="text-gray-400">Time Remaining</div>
                <div className="text-white">{timeRemaining}m</div>
              </div>
            </div>
            
            <div className="mt-4">
              <div className="text-sm font-semibold text-white mb-2">Set Result</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedOutcome('WIN')}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                    selectedOutcome === 'WIN' 
                      ? 'bg-emerald-500/30 border-emerald-500 text-white' 
                      : 'border-gray-700 text-gray-300 hover:border-emerald-500/50'
                  }`}
                >
                  <TrendingUp size={16} className="inline mr-1" /> WIN
                </button>
                <button
                  onClick={() => setSelectedOutcome('LOSE')}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                    selectedOutcome === 'LOSE' 
                      ? 'bg-red-500/30 border-red-500 text-white' 
                      : 'border-gray-700 text-gray-300 hover:border-red-500/50'
                  }`}
                >
                  <TrendingDown size={16} className="inline mr-1" /> LOSE
                </button>
                <button
                  onClick={() => setSelectedOutcome('PUSH')}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${
                    selectedOutcome === 'PUSH' 
                      ? 'bg-blue-500/30 border-blue-500 text-white' 
                      : 'border-gray-700 text-gray-300 hover:border-blue-500/50'
                  }`}
                >
                  <Minus size={16} className="inline mr-1" /> PUSH
                </button>
              </div>
              
              <div className="mt-3">
                <input
                  type="text"
                  placeholder="Optional note..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-white text-sm focus:border-primary-500 focus:outline-none"
                />
              </div>
              
              <button
                onClick={handleConfirm}
                disabled={!selectedOutcome}
                className={`mt-3 px-6 py-2 rounded-lg text-sm font-semibold transition ${
                  selectedOutcome 
                    ? 'bg-primary-500 hover:bg-primary-600 text-white' 
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                Confirm Result
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Main component
export default function AdminTradeManagement() {
  const [activeTab, setActiveTab] = useState('PENDING'); // PENDING | ALL
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  
  const queryClient = useQueryClient();
  
  // Fetch pending trades
  const pendingQuery = useQuery({
    queryKey: ['adminPendingTrades'],
    queryFn: adminApi.getPendingTrades,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  // Fetch all trades with filters
  const allTradesQuery = useQuery({
    queryKey: ['adminAllTrades', page, pageSize, statusFilter, searchTerm],
    queryFn: () => adminApi.getAllTrades({
      page,
      pageSize,
      status: statusFilter,
      search: searchTerm,
    }),
    enabled: activeTab === 'ALL',
  });
  
  // Set result mutation
  const setResultMutation = useMutation({
    mutationFn: ({ tradeId, outcome, note }) => 
      adminApi.setTradeResult(tradeId, { outcome, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPendingTrades'] });
      queryClient.invalidateQueries({ queryKey: ['adminAllTrades'] });
    },
  });
  
  const handleSetResult = (tradeId, outcome, note) => {
    if (window.confirm(`Set trade outcome to ${outcome}?`)) {
      setResultMutation.mutate({ tradeId, outcome, note });
    }
  };
  
  const pendingTrades = pendingQuery.data || [];
  const allTrades = allTradesQuery.data?.items || [];
  const totalAllTrades = allTradesQuery.data?.total || 0;
  const totalPages = Math.ceil(totalAllTrades / pageSize);
  
  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Trade Management</h1>
          <p className="text-sm text-gray-400 mt-1">
            Approve trades and set results for expired positions
          </p>
        </div>
        <button
          onClick={() => {
            pendingQuery.refetch();
            allTradesQuery.refetch();
          }}
          className="px-4 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 transition"
        >
          <RefreshCw size={16} className="inline mr-2" />
          Refresh
        </button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="text-gray-400 text-sm">Pending Approval</div>
          <div className="text-2xl font-bold text-white">
            {pendingTrades.filter(t => t.status === 'PENDING_MODERATION').length}
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="text-gray-400 text-sm">Awaiting Result</div>
          <div className="text-2xl font-bold text-yellow-300">
            {pendingTrades.filter(t => t.status === 'APPROVED').length}
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
          <div className="text-gray-400 text-sm">Today's Settlements</div>
          <div className="text-2xl font-bold text-green-300">
            {allTrades.filter(t => t.status === 'RESOLVED' && 
              new Date(t.resolvedAt).toDateString() === new Date().toDateString()
            ).length}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setActiveTab('PENDING')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            activeTab === 'PENDING'
              ? 'bg-primary-500/30 border-primary-500/40 text-white'
              : 'bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700'
          }`}
        >
          <Clock size={16} className="inline mr-2" />
          Pending Decisions ({pendingTrades.filter(t => t.status === 'APPROVED').length})
        </button>
        <button
          onClick={() => setActiveTab('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            activeTab === 'ALL'
              ? 'bg-primary-500/30 border-primary-500/40 text-white'
              : 'bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700'
          }`}
        >
          <Search size={16} className="inline mr-2" />
          All Trades
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'PENDING' ? (
        <div className="space-y-4">
          {pendingQuery.isLoading && <div className="text-gray-300">Loading pending trades...</div>}
          {pendingQuery.isError && (
            <div className="text-red-300">Error: {pendingQuery.error?.message}</div>
          )}
          {!pendingQuery.isLoading && !pendingQuery.isError && pendingTrades.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
              <div className="text-lg">No pending trades</div>
              <div className="text-sm mt-1">All trades are either resolved or awaiting approval</div>
            </div>
          )}
          {pendingTrades.map(trade => (
            <PendingTradeCard 
              key={trade.id} 
              trade={trade} 
              onSetResult={handleSetResult}
            />
          ))}
        </div>
      ) : (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Search by market or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-white text-sm flex-1 min-w-[200px] focus:border-primary-500 focus:outline-none"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-white text-sm focus:border-primary-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          
          {/* Table */}
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/50 text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Market</th>
                    <th className="px-4 py-3 text-left">User</th>
                    <th className="px-4 py-3 text-left">Direction</th>
                    <th className="px-4 py-3 text-left">Stake</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Outcome</th>
                    <th className="px-4 py-3 text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {allTradesQuery.isLoading && (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-400">Loading...</td></tr>
                  )}
                  {allTradesQuery.isError && (
                    <tr><td colSpan="7" className="text-center py-8 text-red-300">Error loading trades</td></tr>
                  )}
                  {!allTradesQuery.isLoading && !allTradesQuery.isError && allTrades.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-400">No trades found</td></tr>
                  )}
                  {!allTradesQuery.isLoading && !allTradesQuery.isError && allTrades.map(trade => (
                    <tr key={trade.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="px-4 py-3 text-white font-medium">{trade.market}</td>
                      <td className="px-4 py-3 text-gray-300">{trade.userId?.slice(0, 8)}...</td>
                      <td className="px-4 py-3">
                        <span className={trade.direction === 'UP' ? 'text-green-300' : 'text-red-300'}>
                          {trade.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">${Number(trade.stakeAmount).toLocaleString()}</td>
                      <td className="px-4 py-3"><StatusBadge status={trade.status} /></td>
                      <td className="px-4 py-3"><OutcomeBadge outcome={trade.outcome} /></td>
                      <td className="px-4 py-3 text-gray-300 text-xs">
                        {new Date(trade.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-4 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
